import React, {
  CSSProperties,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import type { PopoverProps } from "antd/es/popover";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import LogoutOutlined from "@ant-design/icons/LogoutOutlined";
import SettingOutlined from "@ant-design/icons/SettingOutlined";
import Popover from "common/Popover/Popover";
import Button from "common/Buttons/Button";
import UserAvatar from "common/Avatars/UserAvatar";
import DarkModeToggle from "common/Home/Header/DarkModeToggle";
import useLogout from "utils/hooks/useLogout";
import { AuthUser, LanguageNameSpaces } from "@shared/types";
import useMedia from "utils/hooks/useMedia";
import {
  IS_WHOP,
  MOBILE_SEARCH,
  SECURITY_SETTINGS_PATH,
} from "common/constants";
import Flex from "common/Boxes/Flex";
import { useHistory } from "react-router-dom";
import {
  USER_AVATAR_EMAIL_ID,
  USER_AVATAR_TRIGGER_ID,
  USER_LOGOUT_BUTTON_ID,
} from "testIds.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import LanguageSelector from "common/Home/Header/LanguageSelector.tsx";
import NewLookToggle from "features/newLook/NewLookToggle.tsx";
import useCanToggleNewLook from "features/newLook/useCanToggleNewLook.ts";
import { useUserInfo } from "@shared/utils/hooks/misc.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants.ts";
import { LogOut, Settings } from "lucide-react";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface UserInfoProps {
  user: AuthUser;
  toggleMobileMenu?: () => void;
  style?: CSSProperties;
  hideSettings?: boolean;
  trigger?: ReactNode;
  placement?: PopoverProps["placement"];
}

const UserInfo: React.FC<UserInfoProps> = ({
  user,
  toggleMobileMenu,
  style,
  hideSettings,
  trigger,
  placement = "bottomRight",
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();
  const [logout] = useLogout();
  const history = useHistory();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { userInfoItems, uidItem } = useUserInfo();
  const { isNewLook } = useTheme();
  const canToggleNewLook = useCanToggleNewLook();

  useEffect(() => {
    if (!popoverOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      const popoverElement = document.querySelector(
        `.${USER_INFO_POPOVER_CLASS}`
      );
      const clickedTrigger = triggerRef.current?.contains(target);
      const clickedPopover = popoverElement?.contains(target);

      if (!clickedTrigger && !clickedPopover) {
        setPopoverOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [popoverOpen]);

  const dismissWithEscape = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== "Escape" || !popoverOpen) return;
    event.stopPropagation();
    setPopoverOpen(false);
    triggerRef.current?.focus();
  };

  const popoverContent = (
    <div id={popoverId} onKeyDown={dismissWithEscape}>
      <TitleWrapper>
        <UserAvatar
          user={user}
          avatarSize={isNewLook ? 56 : 80}
          fallbackInitialsSize={isNewLook ? 3 : 1}
        />
        <Title
          level={3}
          style={{ marginTop: 12 }}
          data-testid={USER_AVATAR_EMAIL_ID}
        >
          {user.email || user.full_name}
        </Title>
      </TitleWrapper>
      {uidItem && (
        <Properties>
          <PropertyWrapper key={uidItem.key}>
            <Text>{uidItem.key}</Text>
            <Text type={uidItem.type}>{uidItem.value}</Text>
          </PropertyWrapper>
        </Properties>
      )}
      {!!userInfoItems.length && (
        <Properties>
          {userInfoItems.map((item) => {
            return (
              <PropertyWrapper key={item.key}>
                <Text>{item.key}</Text>
                <Text type={item.type}>{item.value}</Text>
              </PropertyWrapper>
            );
          })}
        </Properties>
      )}
      <Properties $hideBorder={IS_WHOP}>
        {!IS_WHOP && (
          <PropertyWrapper>
            <DarkModeToggle />
          </PropertyWrapper>
        )}
        <PropertyWrapper>
          <LanguageSelector />
        </PropertyWrapper>
        {canToggleNewLook && !user.is_onboarding && (
          <PropertyWrapper>
            <NewLookToggle />
          </PropertyWrapper>
        )}
      </Properties>
      {!user.isWhopExperience() && (
        <ButtonWrapper>
          {!IS_TELEGRAM_WEB_APP && !IS_WHOP && (
            <StyledButton onClick={logout} data-testid={USER_LOGOUT_BUTTON_ID}>
              <LogoutOutlined />
              {t("user_info.logout_button")}
            </StyledButton>
          )}
          {!hideSettings && (
            <StyledButton
              onClick={() => history.push(SECURITY_SETTINGS_PATH)}
              $colored={true}
            >
              <SettingOutlined />
              {t("user_info.settings_button")}
            </StyledButton>
          )}
        </ButtonWrapper>
      )}
    </div>
  );

  // New look: a compact account menu — who is signed in, the plan facts as
  // label/value rows, the preferences, then the two actions as menu rows.
  const infoItems = [...(uidItem ? [uidItem] : []), ...userInfoItems];
  const showLogout = !IS_TELEGRAM_WEB_APP && !IS_WHOP;
  const showActions = !user.isWhopExperience() && (showLogout || !hideSettings);
  const newLookPopoverContent = (
    <NewLookMenu id={popoverId} onKeyDown={dismissWithEscape}>
      <NewLookHeader>
        <UserAvatar user={user} avatarSize={36} fallbackInitialsSize={4} />
        <NewLookIdentity>
          {user.full_name && user.email && (
            <NewLookName>{user.full_name}</NewLookName>
          )}
          <NewLookEmail
            $isPrimary={!(user.full_name && user.email)}
            data-testid={USER_AVATAR_EMAIL_ID}
          >
            {user.email || user.full_name}
          </NewLookEmail>
        </NewLookIdentity>
      </NewLookHeader>
      {!!infoItems.length && (
        <NewLookSection>
          {infoItems.map((item) => (
            <NewLookInfoRow key={item.key}>
              <NewLookInfoLabel>{item.key}</NewLookInfoLabel>
              <NewLookInfoValue $danger={item.type === "danger"}>
                {item.value}
              </NewLookInfoValue>
            </NewLookInfoRow>
          ))}
        </NewLookSection>
      )}
      <NewLookSection>
        {!IS_WHOP && (
          <NewLookPreferenceRow>
            <DarkModeToggle />
          </NewLookPreferenceRow>
        )}
        <NewLookPreferenceRow>
          <LanguageSelector />
        </NewLookPreferenceRow>
        {canToggleNewLook && !user.is_onboarding && (
          <NewLookPreferenceRow>
            <NewLookToggle />
          </NewLookPreferenceRow>
        )}
      </NewLookSection>
      {showActions && (
        <NewLookSection>
          {!hideSettings && (
            <NewLookMenuItem
              type="button"
              onClick={() => {
                setPopoverOpen(false);
                history.push(SECURITY_SETTINGS_PATH);
              }}
            >
              <Settings size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
              {t("user_info.settings_button")}
            </NewLookMenuItem>
          )}
          {showLogout && (
            <NewLookMenuItem
              type="button"
              onClick={logout}
              data-testid={USER_LOGOUT_BUTTON_ID}
            >
              <LogOut size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
              {t("user_info.logout_button")}
            </NewLookMenuItem>
          )}
        </NewLookSection>
      )}
    </NewLookMenu>
  );

  const userAvatar = (
    <AccountTrigger
      type="button"
      style={style}
      onClick={toggleMobileMenu}
      onKeyDown={dismissWithEscape}
      ref={triggerRef}
      aria-label={t("user_info.account_menu_label")}
      aria-expanded={toggleMobileMenu ? undefined : popoverOpen}
      aria-controls={popoverOpen ? popoverId : undefined}
      data-testid={USER_AVATAR_TRIGGER_ID}
    >
      {trigger ?? (
        <UserAvatar
          user={user}
          avatarSize={isMobile ? 32 : 40}
          fallbackInitialsSize={4}
        />
      )}
    </AccountTrigger>
  );

  if (toggleMobileMenu) {
    return userAvatar;
  }

  return (
    <Popover
      placement={placement}
      title={false}
      content={isNewLook ? newLookPopoverContent : popoverContent}
      trigger="click"
      open={popoverOpen}
      onOpenChange={setPopoverOpen}
      classNames={{ root: USER_INFO_POPOVER_CLASS }}
      styles={
        isNewLook
          ? { root: { width: 300 }, container: { padding: 0 } }
          : { root: { minWidth: 300 } }
      }
    >
      {userAvatar}
    </Popover>
  );
};

export default UserInfo;

const USER_INFO_POPOVER_CLASS = "user-info-popover";

const AccountTrigger = styled.button`
  display: block;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: inherit;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.primaryBlue};
    outline-offset: 3px;
  }
`;
const TitleWrapper = styled.div`
  padding: 16px 0 20px;
  display: flex;
  align-items: center;
  flex-direction: column;
  border-bottom: ${(p) => p.theme.borders.light};
  ${(p) =>
    p.theme.isNewLook &&
    `
    padding: 12px 0 16px;
    border-bottom: ${p.theme.borders.lightest};
    h3 {
      font-size: 14px;
    }
  `}
`;
const Properties = styled.div<{ $hideBorder?: boolean }>`
  padding: 16px 8px 6px;
  ${(p) =>
    !p.$hideBorder &&
    `
    border-bottom: ${
      p.theme.isNewLook ? p.theme.borders.lightest : p.theme.borders.light
    };
    padding: ${p.theme.isNewLook ? "12px 4px" : "16px 8px"};
  `};
`;
const PropertyWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;
const ButtonWrapper = styled(Flex)`
  width: 100%;
  position: relative;
  padding: 20px 0 10px;
  column-gap: 8px;
`;
const StyledButton = styled(Button)<{ $colored?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 1;
  ${(p) =>
    p.$colored &&
    `
    border-color: ${p.theme.colors.primaryBlue} !important;
    color: ${p.theme.colors.primaryBlue} !important;
  `}
`;
const NewLookMenu = styled.div`
  display: flex;
  flex-direction: column;
`;
const NewLookHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
`;
const NewLookIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const NewLookName = styled.div`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NewLookEmail = styled.div<{ $isPrimary: boolean }>`
  ${(p) => (p.$isPrimary ? newLookType.panelTitle : newLookType.meta)};
  color: ${(p) =>
    p.$isPrimary
      ? getNewLookTokens(p.theme).ink
      : getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
/** A hairline-topped group of rows. */
const NewLookSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 0;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const NewLookInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 32px;
  padding: 0 16px;
`;
const NewLookInfoLabel = styled.span`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookInfoValue = styled.span<{ $danger: boolean }>`
  ${newLookType.body};
  color: ${(p) =>
    p.$danger
      ? getNewLookTokens(p.theme).down
      : getNewLookTokens(p.theme).text};
  font-variant-numeric: tabular-nums;
  text-align: end;
`;
const NewLookPreferenceRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 0 16px;
  .ant-typography {
    ${newLookType.body};
    color: ${(p) => getNewLookTokens(p.theme).text};
  }
`;
const NewLookMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: calc(100% - 12px);
  min-height: 36px;
  margin: 0 6px;
  padding: 0 10px;
  border: 0;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: transparent;
  ${newLookType.body};
  font-family: inherit;
  color: ${(p) => getNewLookTokens(p.theme).text};
  text-align: start;
  cursor: pointer;
  transition: background-color 120ms;
  svg {
    flex: none;
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
  }
`;
